import { Pencil, Plus, Star, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { tAny } from '@/i18n'
import { cn } from '@/utils/cn'
import { formatDate } from '@/utils/format'
import { GUARDIAN_RELATIONS } from '../status'
import type { PublicAdmissions } from '../types'
import { emptyGuardian, formatBytes, type ApplicationForm, type StepKey, type UploadedDocs } from '../wizard'
import { DocumentUpload } from './DocumentUpload'

const GENDERS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
]

/** Step 1: the class applied for, with places left and what each série means. */
export function ClassStep({ info }: { info: PublicAdmissions }) {
  const { t } = useTranslation('admissions')
  const {
    control,
    formState: { errors },
  } = useFormContext<ApplicationForm>()
  return (
    <Controller
      name="classId"
      control={control}
      render={({ field }) => (
        <fieldset aria-describedby={errors.classId ? 'class-error' : undefined}>
          <legend className="mb-3 text-sm text-muted">
            {t('Choose the class the student will join in {{year}}.', {
              year: info.academicYear?.name ?? '',
            })}
          </legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" role="radiogroup">
            {info.classes.map((c) => {
              const full = c.placesLeft === 0
              const selected = field.value === String(c.id)
              return (
                <label
                  key={c.id}
                  className={cn(
                    'relative flex min-w-0 cursor-pointer flex-col gap-1.5 rounded-card border bg-surface p-4 shadow-xs transition',
                    selected
                      ? 'border-brand-500 ring-2 ring-brand-200'
                      : 'border-line hover:border-brand-300',
                    full && 'cursor-not-allowed opacity-60',
                  )}
                >
                  <input
                    type="radio"
                    name={field.name}
                    value={String(c.id)}
                    checked={selected}
                    disabled={full}
                    onChange={() => field.onChange(String(c.id))}
                    onBlur={field.onBlur}
                    className="sr-only"
                  />
                  <span className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="min-w-0 font-semibold break-words text-ink">{c.name}</span>
                    {c.series ? (
                      <Badge tone="brand" size="sm">
                        {t('Série {{series}}', { series: c.series })}
                      </Badge>
                    ) : null}
                  </span>
                  {c.description ? <span className="text-sm text-ink-soft">{c.description}</span> : null}
                  <span className={cn('text-xs font-medium', full ? 'text-danger-600' : 'text-muted')}>
                    {c.placesLeft === null
                      ? t('Places available')
                      : full
                        ? t('Full')
                        : t('{{count}} places left', { count: c.placesLeft })}
                  </span>
                </label>
              )
            })}
          </div>
          {errors.classId?.message ? (
            <p id="class-error" role="alert" className="mt-2 text-xs font-medium text-danger-600">
              {tAny(errors.classId.message)}
            </p>
          ) : null}
        </fieldset>
      )}
    />
  )
}

/** Step 2: the student. */
export function StudentStep({ info }: { info: PublicAdmissions }) {
  const { t } = useTranslation('admissions')
  const {
    register,
    formState: { errors },
  } = useFormContext<ApplicationForm>()
  const e = errors.student
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FormField label={t('First name')} error={e?.firstName?.message} required>
        <Input autoComplete="off" {...register('student.firstName')} />
      </FormField>
      <FormField label={t('Last name')} error={e?.lastName?.message} required>
        <Input autoComplete="off" {...register('student.lastName')} />
      </FormField>
      <FormField label={t('Gender')} error={e?.gender?.message} required>
        <Select
          placeholder={t('Choose…')}
          options={GENDERS.map((g) => ({ value: g.value, label: t(g.label) }))}
          {...register('student.gender')}
        />
      </FormField>
      <FormField label={t('Date of birth')} error={e?.dateOfBirth?.message} required>
        <Input type="date" {...register('student.dateOfBirth')} />
      </FormField>
      <FormField label={t('Place of birth')} error={e?.placeOfBirth?.message} required>
        <Input {...register('student.placeOfBirth')} />
      </FormField>
      <FormField label={t('Nationality')} error={e?.nationality?.message} required>
        <Input {...register('student.nationality')} />
      </FormField>
      <FormField label={t('Home address')} error={e?.address?.message} required className="sm:col-span-2">
        <Textarea rows={2} {...register('student.address')} />
      </FormField>
      <FormField
        label={t('Student’s phone')}
        error={e?.phone?.message}
        required={!info.guardiansEnabled}
        hint={info.guardiansEnabled ? t('Optional') : undefined}
      >
        <Input type="tel" autoComplete="tel" {...register('student.phone')} />
      </FormField>
      <FormField label={t('Student’s email')} error={e?.email?.message} hint={t('Optional')}>
        <Input type="email" autoComplete="email" {...register('student.email')} />
      </FormField>
    </div>
  )
}

/** Step 3: one to three parents or guardians; at least one phone number; one main contact. */
export function GuardiansStep() {
  const { t } = useTranslation('admissions')
  const {
    control,
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<ApplicationForm>()
  const { fields, append, remove } = useFieldArray({ control, name: 'guardians' })
  const makePrimary = (index: number) =>
    getValues('guardians').forEach((_, i) =>
      setValue(`guardians.${i}.isPrimary`, i === index, { shouldDirty: true }),
    )

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        {t('Add up to three parents or guardians. The main contact receives our emails and calls.')}
      </p>
      {errors.guardians?.root?.message || errors.guardians?.message ? (
        <Alert tone="danger">{tAny(errors.guardians.root?.message ?? errors.guardians.message ?? '')}</Alert>
      ) : null}
      {fields.map((f, i) => {
        const ge = errors.guardians?.[i]
        const primary = getValues(`guardians.${i}.isPrimary`)
        return (
          <fieldset key={f.id} className="min-w-0 rounded-card border border-line bg-surface p-4">
            <legend className="sr-only">{t('Parent or guardian {{n}}', { n: i + 1 })}</legend>
            <div className="mb-3 flex min-w-0 flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-ink">
                {t('Parent or guardian {{n}}', { n: i + 1 })}
              </span>
              {primary ? (
                <Badge tone="brand" size="sm">
                  <Star aria-hidden="true" />
                  {t('Main contact')}
                </Badge>
              ) : (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => makePrimary(i)}
                  className="h-auto whitespace-normal"
                >
                  {t('Make main contact')}
                </Button>
              )}
              {fields.length > 1 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  leftIcon={<Trash2 />}
                  onClick={() => {
                    remove(i)
                    if (primary) setTimeout(() => makePrimary(0))
                  }}
                >
                  {t('Remove')}
                </Button>
              ) : null}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label={t('Relation')} error={ge?.relation?.message} required>
                <Select
                  placeholder={t('Choose…')}
                  options={GUARDIAN_RELATIONS.map((r) => ({ value: r.value, label: t(r.label) }))}
                  {...register(`guardians.${i}.relation`)}
                />
              </FormField>
              <FormField label={t('Full name')} error={ge?.name?.message} required>
                <Input autoComplete="off" {...register(`guardians.${i}.name`)} />
              </FormField>
              <FormField label={t('Phone')} error={ge?.phone?.message}>
                <Input type="tel" {...register(`guardians.${i}.phone`)} />
              </FormField>
              <FormField label={t('Email')} error={ge?.email?.message}>
                <Input type="email" {...register(`guardians.${i}.email`)} />
              </FormField>
              <FormField label={t('Occupation')} error={ge?.occupation?.message} className="sm:col-span-2">
                <Input {...register(`guardians.${i}.occupation`)} />
              </FormField>
            </div>
          </fieldset>
        )
      })}
      {fields.length < 3 ? (
        <Button
          variant="secondary"
          leftIcon={<Plus />}
          className="h-auto min-h-10 py-2 text-left leading-snug whitespace-normal"
          onClick={() => append(emptyGuardian(fields.length === 0))}
        >
          {t('Add a parent or guardian')}
        </Button>
      ) : null}
    </div>
  )
}

/** Step 4: previous school (when the school asks) and the documents. */
export function DocumentsStep({
  info,
  docs,
  onDocChange,
  docErrors,
}: {
  info: PublicAdmissions
  docs: UploadedDocs
  onDocChange: (index: number, value: UploadedDocs[number] | undefined) => void
  docErrors: Record<number, string>
}) {
  const { t } = useTranslation('admissions')
  const {
    register,
    formState: { errors },
  } = useFormContext<ApplicationForm>()
  const uploads = info.requiredDocuments.filter((d) => d.upload)
  const toBring = info.requiredDocuments.filter((d) => !d.upload)
  return (
    <div className="space-y-6">
      {info.askPreviousSchool ? (
        <section aria-labelledby="previous-heading">
          <h3 id="previous-heading" className="mb-3 text-sm font-semibold text-ink">
            {t('Previous school')}
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label={t('School attended this year')} error={errors.student?.previousSchool?.message}>
              <Input {...register('student.previousSchool')} />
            </FormField>
            <FormField label={t('Class attended this year')} error={errors.student?.previousClass?.message}>
              <Input {...register('student.previousClass')} />
            </FormField>
          </div>
        </section>
      ) : null}

      {uploads.length ? (
        <section aria-labelledby="uploads-heading">
          <h3 id="uploads-heading" className="mb-1 text-sm font-semibold text-ink">
            {t('Documents to send')}
          </h3>
          <p className="mb-3 text-xs text-muted">
            {t('A photo taken with a phone is fine, as long as it is readable.')}
          </p>
          <div className="grid grid-cols-1 gap-3">
            {uploads.map((d) => (
              <DocumentUpload
                key={d.index}
                doc={d}
                value={docs[d.index]}
                onChange={(v) => onDocChange(d.index, v)}
                maxBytes={info.upload.maxBytes}
                types={info.upload.types}
                error={docErrors[d.index]}
              />
            ))}
          </div>
        </section>
      ) : null}

      {toBring.length ? (
        <section
          aria-labelledby="bring-heading"
          className="rounded-card border border-dashed border-line-strong bg-surface-muted/60 p-4"
        >
          <h3 id="bring-heading" className="mb-2 text-sm font-semibold text-ink">
            {t('To bring to the school office')}
          </h3>
          <ul className="space-y-1.5 text-sm text-ink-soft">
            {toBring.map((d) => (
              <li key={d.index} className="min-w-0">
                <span className="font-medium">• {d.label}</span>
                {d.description ? <span className="text-muted"> — {d.description}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}

function ReviewSection({
  title,
  step,
  onEdit,
  children,
}: {
  title: string
  step: StepKey
  onEdit: (step: StepKey) => void
  children: ReactNode
}) {
  const { t } = useTranslation('admissions')
  return (
    <section className="min-w-0 rounded-card border border-line bg-surface p-4">
      <div className="mb-2 flex min-w-0 items-center gap-2">
        <h3 className="min-w-0 flex-1 text-sm font-semibold text-ink">{title}</h3>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Pencil />}
          onClick={() => onEdit(step)}
          aria-label={t('Edit: {{section}}', { section: title })}
        >
          {t('Edit')}
        </Button>
      </div>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-[minmax(0,10rem)_1fr]">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return value ? (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 break-words text-ink">{value}</dd>
    </>
  ) : null
}

/** Step 5: everything entered, with links back to each step, and the consent. */
export function ReviewStep({
  info,
  docs,
  onEdit,
}: {
  info: PublicAdmissions
  docs: UploadedDocs
  onEdit: (step: StepKey) => void
}) {
  const { t } = useTranslation('admissions')
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext<ApplicationForm>()
  const v = getValues()
  const cls = info.classes.find((c) => String(c.id) === v.classId)
  const relation = (r: string) => t(GUARDIAN_RELATIONS.find((x) => x.value === r)?.label ?? 'Other')
  const s = v.student

  return (
    <div className="space-y-4">
      <ReviewSection onEdit={onEdit} title={t('Class')} step="class">
        <Row label={t('Class')} value={cls ? `${cls.name} · ${info.academicYear?.name ?? ''}` : '—'} />
      </ReviewSection>
      <ReviewSection onEdit={onEdit} title={t('Student')} step="student">
        <Row label={t('Name')} value={`${s.firstName} ${s.lastName}`} />
        <Row label={t('Gender')} value={t(GENDERS.find((g) => g.value === s.gender)?.label ?? '')} />
        <Row label={t('Born')} value={`${formatDate(s.dateOfBirth)} · ${s.placeOfBirth}`} />
        <Row label={t('Nationality')} value={s.nationality} />
        <Row label={t('Address')} value={s.address} />
        <Row label={t('Phone')} value={s.phone} />
        <Row label={t('Email')} value={s.email} />
      </ReviewSection>
      {info.guardiansEnabled ? (
        <ReviewSection onEdit={onEdit} title={t('Parents and guardians')} step="guardians">
          {v.guardians.map((g, i) => (
            <Row
              key={i}
              label={relation(g.relation)}
              value={
                <>
                  {g.name}
                  {g.isPrimary ? <span className="text-brand-700"> · {t('Main contact')}</span> : null}
                  <span className="block text-muted">
                    {[g.phone, g.email, g.occupation].filter(Boolean).join(' · ')}
                  </span>
                </>
              }
            />
          ))}
        </ReviewSection>
      ) : null}
      <ReviewSection onEdit={onEdit} title={t('Documents')} step="documents">
        {info.askPreviousSchool ? (
          <Row
            label={t('Previous school')}
            value={[s.previousSchool, s.previousClass].filter(Boolean).join(' · ') || '—'}
          />
        ) : null}
        {info.requiredDocuments
          .filter((d) => d.upload)
          .map((d) => (
            <Row
              key={d.index}
              label={d.label}
              value={
                docs[d.index]
                  ? `${docs[d.index]?.name ?? ''} (${formatBytes(docs[d.index]?.size ?? 0)})`
                  : t('Not sent')
              }
            />
          ))}
      </ReviewSection>

      <Controller
        name="consent"
        control={control}
        render={({ field }) => (
          <div className="rounded-card border border-line bg-surface p-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="consent"
                checked={field.value}
                onCheckedChange={(c) => field.onChange(c === true)}
                aria-invalid={errors.consent ? true : undefined}
                aria-describedby={errors.consent ? 'consent-error' : undefined}
                className="mt-0.5"
              />
              <label htmlFor="consent" className="min-w-0 cursor-pointer text-sm text-ink-soft">
                {t(
                  'I confirm that the information is correct and I agree that the school uses it to process this application.',
                )}
              </label>
            </div>
            {errors.consent?.message ? (
              <p id="consent-error" role="alert" className="mt-2 text-xs font-medium text-danger-600">
                {tAny(errors.consent.message)}
              </p>
            ) : null}
          </div>
        )}
      />
    </div>
  )
}
